import { useContext, useRef, useState } from 'react';
import { ListConductorRawFilter } from '../../../list-conductor/filters';
import { IntegrationsFactory } from '../../../pipelines/factories/integrationFactory';
import { useInMemoryFilteredList } from '../../../shared/hooks/useInMemoryFilteredList';
import { matchFilters } from '../../../shared/utils/filters';
import { getFilterKeyMatchValue, getSortValue } from '../Utils';
import { AddSubscriptionButton } from './AddSubscriptionButton';
import { PipelineSubscriptionsList } from './PipelineSubscriptionsList';
import { UnSubscribeButton } from './UnSubscribeButton';
import { useConfirm } from '../../../modal/ConfirmModal/useConfirm';
import { AddPipelineSubscriptionList } from './AddPipelineSubscriptionList';
import { AlertsAPIservice } from '../../APIServices/APIServiceContext';
import { useDontMountAtFirst } from '../../../shared/hooks/useDontMountAtFirst';

export function PipelineSubscriptionsListWrapper() {
  const entityListRef = useRef<{ reset: () => void } | null>(null);
  const { confirm } = useConfirm();
  const [openAddPipelineModal, setOpenAddPipelineModal] = useState(false);
  const { APIService, activeRecipientID } = useContext(AlertsAPIservice);
  const {
    fetch,
    selectedItems,
    reset,
    onAllInPageSelectionChange,
    onAllItemsSelectionChange,
    onItemSelectionChange,
    onBulkActionCTAClick,
    resetSelectedItems
  } = useInMemoryFilteredList(
    async () => {
      return APIService.getSubscriptions().then(([pipelines, subscribedPipelines]) => {
        const subscribedPipelineIds = new Set(
          subscribedPipelines.data.subscriptions.map(data => data.entity_id)
        );
        const subscribedPipelinesData = pipelines.data.filter(pipeline =>
          subscribedPipelineIds.has(pipeline.id.toString())
        );
        return IntegrationsFactory(subscribedPipelinesData);
      });
    },
    (item, params) => {
      return matchFilters(params.filters as ListConductorRawFilter[], filterKey =>
        getFilterKeyMatchValue(filterKey, item)
      );
    },
    undefined,
    undefined,
    (item, field) => getSortValue(item, field) ?? '',
    false,
    'id',
    undefined,
    async items => {
      confirm({
        title: 'Unsubscribe Pipelines?',
        negativeButtonText: 'Cancel',
        positiveButtonText: 'Unsubscribe',
        body: "You won't receive alerts for unsubscribed Pipelines",
        onPositiveClickCallback: async () => {
          await APIService.unSubscribePipelines(items);
          refetchListData();
        }
      });
    }
  );

  useDontMountAtFirst(() => {
    refetchListData();
  }, [activeRecipientID]);

  const refetchListData = () => {
    reset();
    resetSelectedItems();
    entityListRef?.current?.reset();
  };

  const closeModalHandler = (refetch: boolean) => {
    if (refetch) {
      refetchListData();
    }

    setOpenAddPipelineModal(false);
  };

  return (
    <>
      <PipelineSubscriptionsList
        innerRef={entityListRef}
        StandaloneModeActions={AddSubscriptionButton}
        standaloneModeActionsProps={{
          onAddPipelineClick: () => setOpenAddPipelineModal(true),
          className: 'ml-3'
        }}
        newListConnectedTitle="You haven't subscribed to any Pipeline"
        listIcon='pipeline'
        newListComponentProps={{
          primaryCTAProps: {
            dataId: 'add-pipeline-subscription',
            icon: 'plus',
            onClick: () => {
              setOpenAddPipelineModal(true);
            },
            children: 'Add Subscription'
          }
        }}
        selectedItems={selectedItems}
        onAllInPageSelectionChange={onAllInPageSelectionChange}
        onAllItemsSelectionChange={onAllItemsSelectionChange}
        onItemSelectionChange={onItemSelectionChange}
        entity='subscribed-pipelines'
        fetch={fetch}
        BulkActionsComponent={UnSubscribeButton}
        onRefresh={() => {
          reset();
        }}
        bulkActionsComponentProps={{
          onClick: onBulkActionCTAClick
        }}
      />

      {openAddPipelineModal ? (
        <AddPipelineSubscriptionList open onClose={closeModalHandler} />
      ) : null}
    </>
  );
}
